import React, { useState } from 'react';
import styled from 'styled-components';
import { useInventory } from '../../contexts/InventoryContext';
import toast from 'react-hot-toast';

const Container = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  display: flex;
  justify-content: between;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const FilterBar = styled.div`
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  option {
    background: white;
    color: #333;
  }
`;

const Table = styled.div`
  overflow-x: auto;
  
  /* Custom horizontal scrollbar styling */
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 120px 150px 100px 120px 150px 120px 120px 100px 150px;
  padding: 15px 20px;
  border-bottom: 1px solid #e9ecef;
  align-items: center;
  transition: background 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }

  &.header {
    background: #f1f3f4;
    font-weight: 600;
    color: #495057;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const Cell = styled.div`
  font-size: 14px;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.status) {
      case 'available': return '#d4edda';
      case 'pending_verification': return '#fff3cd';
      case 'in_transit': return '#d1ecf1';
      case 'expired': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'available': return '#155724';
      case 'pending_verification': return '#856404';
      case 'in_transit': return '#0c5460';
      case 'expired': return '#721c24';
      default: return '#495057';
    }
  }};
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 5px;

  &.view {
    background: #17a2b8;
    color: white;
    
    &:hover {
      background: #138496;
    }
  }

  &.edit {
    background: #ffc107;
    color: #212529;
    
    &:hover {
      background: #e0a800;
    }
  }

  &.delete {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }

  &.move {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
    }
  }
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #6c757d;
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
  }
`;

const LoadingState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Pagination = styled.div`
  padding: 20px;
  display: flex;
  justify-content: between;
  align-items: center;
  border-top: 1px solid #e9ecef;
`;

const PageInfo = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

const PageControls = styled.div`
  display: flex;
  gap: 8px;
`;

const PageButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #f8f9fa;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.active {
    background: #667eea;
    color: white;
    border-color: #667eea;
  }
`;

const InventoryItemList = () => {
  const { inventoryItems, loading, moveInventory } = useInventory();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [commodityFilter, setCommodityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const itemsPerPage = 10;

  // Filter items based on search and filters
  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = item.commodityType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.batchNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesCommodity = commodityFilter === 'all' || item.commodityType === commodityFilter;
    
    return matchesSearch && matchesStatus && matchesCommodity;
  });

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  // Get unique commodity types for filter
  const commodityTypes = [...new Set(inventoryItems.map(item => item.commodityType))];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const handleViewItem = (item) => {
    toast.success(`Viewing details for ${item.commodityType} ${item.id}`);
    // TODO: Open modal with item details
  };

  const handleEditItem = (item) => {
    toast.success(`Editing ${item.commodityType} ${item.id}`);
    // TODO: Open edit modal
  };

  const handleDeleteItem = (item) => {
    if (window.confirm(`Are you sure you want to delete ${item.commodityType} ${item.id}?`)) {
      toast.success(`Deleted ${item.commodityType} ${item.id}`);
      // TODO: Implement delete functionality
    }
  };

  const handleMoveItem = (item) => {
    toast.success(`Initiating move for ${item.commodityType} ${item.id}`);
    // TODO: Open move modal
  };

  if (loading) {
    return (
      <Container>
        <TableHeader>
          <Title>📦 Inventory Items</Title>
        </TableHeader>
        <LoadingState>
          <Spinner />
          <div>Loading inventory items...</div>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <TableHeader>
        <Title>📦 Inventory Items ({filteredItems.length})</Title>
      </TableHeader>

      {/* Filters */}
      <FilterBar>
        <FilterInput
          type="text"
          placeholder="Search by ID, commodity, or batch..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="available">Available</option>
          <option value="pending_verification">Pending Verification</option>
          <option value="in_transit">In Transit</option>
          <option value="expired">Expired</option>
        </FilterSelect>

        <FilterSelect
          value={commodityFilter}
          onChange={(e) => setCommodityFilter(e.target.value)}
        >
          <option value="all">All Commodities</option>
          {commodityTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </FilterSelect>
      </FilterBar>

      {/* Table */}
      <Table>
        {/* Header */}
        <TableRow className="header">
          <Cell>Item ID</Cell>
          <Cell>Commodity</Cell>
          <Cell>Grade</Cell>
          <Cell>Quantity</Cell>
          <Cell>Location</Cell>
          <Cell>Status</Cell>
          <Cell>Value</Cell>
          <Cell>Tokens</Cell>
          <Cell>Actions</Cell>
        </TableRow>

        {/* Data Rows */}
        {paginatedItems.length > 0 ? (
          paginatedItems.map(item => (
            <TableRow key={item.id}>
              <Cell style={{ fontWeight: '600', color: '#667eea' }}>
                {item.id}
              </Cell>
              <Cell>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>
                    {item.commodityType === 'Sugar' && '🍯'}
                    {item.commodityType === 'Coffee' && '☕'}
                    {item.commodityType === 'Gold' && '🥇'}
                    {item.commodityType === 'Silver' && '🥈'}
                  </span>
                  {item.commodityType}
                </div>
              </Cell>
              <Cell>{item.grade}</Cell>
              <Cell>{formatNumber(item.quantity)} {item.unit}</Cell>
              <Cell>{item.location}</Cell>
              <Cell>
                <StatusBadge status={item.status}>
                  {item.status.replace('_', ' ')}
                </StatusBadge>
              </Cell>
              <Cell style={{ fontWeight: '600' }}>
                {formatCurrency(item.totalValue)}
              </Cell>
              <Cell>{formatNumber(item.tokensMinted)}</Cell>
              <Cell>
                <ActionButton className="view" onClick={() => handleViewItem(item)}>
                  👁️ View
                </ActionButton>
                <ActionButton className="edit" onClick={() => handleEditItem(item)}>
                  ✏️ Edit
                </ActionButton>
                <ActionButton className="move" onClick={() => handleMoveItem(item)}>
                  🚚 Move
                </ActionButton>
                <ActionButton className="delete" onClick={() => handleDeleteItem(item)}>
                  🗑️ Delete
                </ActionButton>
              </Cell>
            </TableRow>
          ))
        ) : (
          <EmptyState>
            <div className="icon">📦</div>
            <div className="title">No Inventory Items Found</div>
            <div className="description">
              {searchTerm || statusFilter !== 'all' || commodityFilter !== 'all'
                ? 'Try adjusting your filters to see more items.'
                : 'Start by adding your first inventory item.'
              }
            </div>
          </EmptyState>
        )}
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          <PageInfo>
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredItems.length)} of {filteredItems.length} items
          </PageInfo>
          
          <PageControls>
            <PageButton
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </PageButton>
            <PageButton
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PageButton>
            
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              const page = Math.max(1, currentPage - 2) + index;
              if (page <= totalPages) {
                return (
                  <PageButton
                    key={page}
                    className={currentPage === page ? 'active' : ''}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </PageButton>
                );
              }
              return null;
            })}
            
            <PageButton
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </PageButton>
            <PageButton
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </PageButton>
          </PageControls>
        </Pagination>
      )}
    </Container>
  );
};

export default InventoryItemList; 